import React from 'react';
import {
  ListRenderItem,
  Image as RNImage,
  TextInput as RNTextInput,
  StyleSheet,
  View,
} from 'react-native';

import { UserAvatar } from '@components/Avatar';
import Chip from '@components/Chip';
import { ConfirmDeleteDialogButton } from '@components/Dialog';
import FlatList from '@components/FlatList';
import { createStringValidator, useAppForm } from '@components/Form';
import { EmptySearchPlaceholder } from '@components/Placeholders';
import ScreenContainer from '@components/ScreenContainer';
import { TextInput, TextInputIcon } from '@components/TextInput';
import { Caption, Title } from '@components/Typography';
import { LargeUserCardList } from '@components/User/CardList';
import { useAppTheme } from '@context/ThemeContext';
import {
  MAX_GROUP_DESCRIPTION_LENGTH,
  validateGroupDescription,
  validateGroupDisplayName,
} from '@db/Groups';
import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGroupAdminsQuery,
  useGroupQuery,
  useUpdateGroupMutation,
} from '@db/Groups/Queries';
import { useUserSearch } from '@db/Search';
import { errorAlert } from '@helpers/Alert';
import { logError } from '@helpers/Loggers';
import { getCurrentUser } from '@helpers/firebase/Auth';
import { resetToHomeWithReload } from '@navigation/Actions';
import { CommonRoute } from '@navigation/Routes';
import { CreateGroupScreenProps, UpdateGroupScreenProps } from '@navigation/Types';
import { Photo, UserInfo } from '@types';

const AVATAR_SIZE = 25;

export function CreateGroupScreen({ navigation }: CreateGroupScreenProps) {
  return (
    <GroupForm
      onComplete={(groupId) => navigation.replace(CommonRoute.ViewGroup, { groupId })}
      onDeleteComplete={() => navigation.popToTop()}
      showCreateButton
      showUpdateButton={false}
      showDeleteButton={false}
    />
  );
}

export function UpdateGroupScreen({ route, navigation }: UpdateGroupScreenProps) {
  return (
    <GroupForm
      groupId={route.params.groupId}
      onComplete={(groupId) =>
        navigation.navigate(CommonRoute.ViewGroup, { groupId, reload: true })
      }
      onDeleteComplete={() => resetToHomeWithReload(navigation)}
      showCreateButton={false}
      showUpdateButton
      showDeleteButton
    />
  );
}

type GroupFormProps = {
  groupId?: string;
  onComplete: (groupId: string) => void;
  onDeleteComplete: () => void;
  showCreateButton: boolean;
  showUpdateButton: boolean;
  showDeleteButton: boolean;
};

type GroupFormValues = {
  admins: UserInfo[];
  description: string;
  displayName: string;
  image?: Photo;
};

type GroupSubmitMeta = {
  action: 'create' | 'update';
};

function GroupForm({
  groupId,
  onComplete,
  onDeleteComplete,
  showCreateButton,
  showUpdateButton,
  showDeleteButton,
}: GroupFormProps) {
  const currentUser = getCurrentUser();
  const defaultAdmins: UserInfo[] = [
    {
      userId: currentUser.uid,
      displayName: currentUser.displayName,
      photoUrl: currentUser.photoURL ?? '',
    },
  ];
  const [searchText, setSearchText] = React.useState('');
  const { results: usersResult } = useUserSearch({ query: searchText });
  const descriptionRef = React.useRef<RNTextInput>(null);
  const appTheme = useAppTheme();
  const groupQuery = useGroupQuery({ groupId });
  const groupAdminsQuery = useGroupAdminsQuery({ group: groupQuery.group });
  const initializedGroupIdRef = React.useRef<string | undefined>(undefined);
  const initializedAdminGroupIdRef = React.useRef<string | undefined>(undefined);
  const loading = groupQuery.loading || groupAdminsQuery.loading;
  const error = groupQuery.error || groupAdminsQuery.error;
  const errorProps = groupQuery.error ? groupQuery.errorProps : groupAdminsQuery.errorProps;
  const createGroupMutation = useCreateGroupMutation();
  const updateGroupMutation = useUpdateGroupMutation();
  const deleteGroupMutation = useDeleteGroupMutation();

  const users = searchText ? usersResult : [];

  const form = useAppForm({
    defaultValues: {
      admins: defaultAdmins,
      description: '',
      displayName: '',
      image: undefined,
    } as GroupFormValues,
    onSubmitMeta: { action: 'create' } as GroupSubmitMeta,
    onSubmit: async ({ meta, value }) => {
      try {
        if (meta.action === 'create') {
          const groupId = await createGroupMutation.mutateAsyncOrThrow({
            group: {
              description: value.description,
              displayName: value.displayName,
              adminIds: value.admins.map((admin) => admin.userId),
            },
            photo: value.image,
          });
          onComplete(groupId);
        } else {
          if (!groupId) {
            throw new Error('Update group form submitted without a group id.');
          }

          await updateGroupMutation.mutateAsyncOrThrow({
            groupId,
            group: {
              displayName: value.displayName,
              description: value.description,
              adminIds: value.admins.map((admin) => admin.userId),
            },
            photo: value.image,
          });
          onComplete(groupId);
        }
      } catch (error) {
        errorAlert(error);
        throw error;
      }
    },
  });

  React.useEffect(() => {
    const group = groupQuery.group;
    if (!group || initializedGroupIdRef.current === group.groupId) return;

    initializedGroupIdRef.current = group.groupId;
    form.setFieldValue('displayName', group.displayName);
    form.setFieldValue('description', group.description);
    if (group.photoUrl) {
      RNImage.getSize(
        group.photoUrl,
        (width, height) => {
          form.setFieldValue('image', { uri: group.photoUrl, width, height });
        },
        (error) => logError(error)
      );
    }
  }, [form, groupQuery.group]);

  React.useEffect(() => {
    if (
      !groupId ||
      !groupQuery.group ||
      groupAdminsQuery.loading ||
      initializedAdminGroupIdRef.current === groupId
    ) {
      return;
    }

    initializedAdminGroupIdRef.current = groupId;
    form.setFieldValue('admins', groupAdminsQuery.admins);
  }, [form, groupAdminsQuery.admins, groupAdminsQuery.loading, groupId, groupQuery.group]);

  const onPressDelete = async () => {
    if (!groupId) {
      errorAlert(new Error('Cannot delete group without a group id.'));
      return;
    }

    try {
      await deleteGroupMutation.mutateAsyncOrThrow({ groupId });
      onDeleteComplete();
    } catch (error) {
      errorAlert(error);
    }
  };

  const userKeyExtractor = (item: UserInfo, index: number) => item.userId;

  const renderNameInput = () => (
    <View style={[styles.inputContainer, styles.top]}>
      <Title>Name</Title>
      <Caption>Choose a clear name people will recognize in invites and search.</Caption>
      <form.AppField
        name="displayName"
        validators={{
          onChange: createStringValidator(validateGroupDisplayName),
          onSubmit: createStringValidator(validateGroupDisplayName),
        }}>
        {(field) => (
          <field.TextInput
            placeholder="Enter a name"
            returnKeyType="next"
            onSubmitEditing={() => descriptionRef.current?.focus()}
            testID="screens.GroupFormScreen.TextInput.DisplayName"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderDescriptionInput = () => (
    <View style={styles.inputContainer}>
      <Title>Description</Title>
      <Caption>Describe who the group is for and what members usually plan together.</Caption>
      <form.AppField
        name="description"
        validators={{
          onChange: createStringValidator(validateGroupDescription),
          onSubmit: createStringValidator(validateGroupDescription),
        }}>
        {(field) => (
          <field.TextInput
            inputRef={descriptionRef}
            placeholder="Enter a description"
            returnKeyType="next"
            maxLength={MAX_GROUP_DESCRIPTION_LENGTH}
            testID="screens.GroupFormScreen.TextInput.Description"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderAdminInput = () => (
    <form.AppField name="admins">
      {(field) => {
        const renderUserChip: ListRenderItem<UserInfo> = ({ item }) => (
          <Chip
            onClose={() =>
              field.handleChange(field.state.value.filter((admin) => admin.userId !== item.userId))
            }
            avatar={
              <UserAvatar
                displayName={item.displayName}
                photoUrl={item.photoUrl}
                size={AVATAR_SIZE}
              />
            }
            style={styles.userChip}>
            {item.displayName}
          </Chip>
        );

        return (
          <View>
            <View style={styles.inputContainer}>
              <Title>Admins</Title>
              <Caption>Admins can edit group details and help manage the member list.</Caption>
              <TextInput
                placeholder="Search people"
                left={<TextInputIcon icon="magnify" />}
                value={searchText}
                onChangeText={(text) => setSearchText(text)}
                style={styles.adminInput}
              />
            </View>
            <FlatList
              data={field.state.value}
              renderItem={renderUserChip}
              keyExtractor={userKeyExtractor}
              horizontal
              contentContainerStyle={styles.selectedAdminListContent}
              style={styles.selectedAdminList}
            />
            <LargeUserCardList
              data={users.slice(0, 3)}
              itemProps={{
                onPress: (user) => {
                  // need to use value passed by setAdmins
                  // due to memoization of user cards
                  field.handleChange((admins) => {
                    const index = admins.findIndex((admin) => admin.userId === user.userId);
                    if (index === -1) {
                      return [...admins, user];
                    } else {
                      return admins;
                    }
                  });
                },
                style: styles.userSurface,
              }}
              ListEmptyComponent={searchText.length > 0 ? <EmptySearchPlaceholder /> : null}
              style={styles.adminList}
            />
          </View>
        );
      }}
    </form.AppField>
  );

  const renderPhotoInput = () => (
    <View style={styles.inputContainer}>
      <Title>Photo</Title>
      <Caption>Add an image that makes this group easy to identify.</Caption>
      <form.AppField name="image">
        {(field) => <field.PhotoInput circle containerStyle={styles.imageContainer} />}
      </form.AppField>
    </View>
  );

  const renderCreateButton = () => (
    <View style={styles.inputContainer}>
      <form.SubmitBigButton
        mode="contained"
        submitMeta={{ action: 'create' } satisfies GroupSubmitMeta}
        testID="screens.GroupFormScreen.Button.Create">
        Create
      </form.SubmitBigButton>
    </View>
  );

  const renderUpdateButton = () => (
    <View style={styles.inputContainer}>
      <form.SubmitBigButton
        mode="contained"
        submitMeta={{ action: 'update' } satisfies GroupSubmitMeta}
        testID="screens.GroupFormScreen.Button.Update">
        Update
      </form.SubmitBigButton>
    </View>
  );

  const renderDeleteButton = () => (
    <View style={styles.inputContainer}>
      <ConfirmDeleteDialogButton
        onDelete={onPressDelete}
        mode="contained"
        buttonColor={appTheme.colors.error}
        message="This will permanently delete the group for all members and can't be undone."
        testID="screens.GroupFormScreen.Button.Delete">
        Delete
      </ConfirmDeleteDialogButton>
    </View>
  );

  return (
    <form.AppForm>
      <ScreenContainer
        loading={loading}
        error={error}
        errorProps={errorProps}
        scrollable
        testID="screens.GroupFormScreen.Background">
        {renderNameInput()}
        {renderDescriptionInput()}
        {renderPhotoInput()}
        {renderAdminInput()}
        {showCreateButton ? renderCreateButton() : null}
        {showUpdateButton ? renderUpdateButton() : null}
        {showDeleteButton ? renderDeleteButton() : null}
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  adminInput: {
    marginVertical: 5,
  },
  adminList: {
    marginHorizontal: 20,
  },
  imageContainer: {
    alignSelf: 'center',
    marginTop: 8,
  },
  inputContainer: {
    marginBottom: 5,
    marginHorizontal: 20,
  },
  selectedAdminList: {
    paddingBottom: 5,
  },
  selectedAdminListContent: {
    paddingHorizontal: 20,
  },
  top: {
    marginTop: 5,
  },
  userChip: {
    marginRight: 2,
  },
  userSurface: {
    marginHorizontal: 0,
  },
});
